import { useMemo } from "react";
import {
  Alert,
  Anchor,
  Box,
  Button,
  Portal,
  Group,
  InputDescription,
  InputLabel,
  MultiSelect,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
  useMantineTheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { FileWithPath } from "@mantine/dropzone";
import { usePathname } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { getJobsIndex, jobsIndexQueryKey } from "@/app/_firebase/jobsIndex";
import { JobBasic, JobBasicSpecimenLineItem } from "@/app/@types/jobBasic";
import { useUserContext } from "@/app/_hooks/useUserContext";
import {
  NewSupportTicketInput,
  SupportRelatedJob,
  SupportTicket,
  SupportTicketCategory,
} from "@/app/@types/support";
import {
  SUPPORT_AFFECTED_AREAS,
  SUPPORT_CATEGORIES,
  getSupportCategory,
} from "@/app/_constants/support";
import useCreateSupportTicket from "@/app/_hooks/support/useCreateSupportTicket";
import { getSupportTrace } from "@/app/_helpers/supportTrace";
import useDeviceType from "@/app/_hooks/useDeviceType";
import { useSetAlert } from "@/app/_hooks/useAlertContext";
import FileUploader from "@/app/_components/FileUploader/FileUploader";
import AnimatedLoader from "@/app/_components/AnimatedLoader/AnimatedLoader";
import SupportFooter from "./SupportFooter";

interface NewSupportTicketFormProps {
  onCreated: (ticket: SupportTicket) => void;
  // Soft duplicate nudge: their unresolved tickets, offered as "add to this
  // instead" links. Never blocks creating a fresh ticket.
  openTickets?: SupportTicket[];
  onOpenTicket?: (ticket: SupportTicket) => void;
}

const MESSAGE_COPY: Record<
  SupportTicketCategory,
  { label: string; description: string; placeholder: string }
> = {
  issue: {
    label: "How does the issue happen?",
    description:
      "Include the exact step where it goes wrong and whether it happens every time or only sometimes.",
    placeholder: `Example:

1. Open Job #32
2. Change the Whitetail buck's location to "Tannery"
3. Click "Update Job"
4. The job is updated but the location is unchanged`,
  },
  suggestion: {
    label: "What would you like to see?",
    description:
      "Let us know the feature or improvement you would like, and the problem it would solve for you.",
    placeholder:
      "e.g. An improvement to a certain feature, or a new feature that you want to see",
  },
  question: {
    label: "What do you need help with?",
    description:
      "Tell us what you are trying to do and where you got stuck. Include a Job # or client name if relevant.",
    placeholder: "e.g. How do I move a payment from one job to another?",
  },
};

const NewSupportTicketForm = ({
  onCreated,
  openTickets = [],
  onOpenTicket,
}: NewSupportTicketFormProps) => {
  const theme = useMantineTheme();
  const pathname = usePathname();
  const { isMobile } = useDeviceType();
  const setAlert = useSetAlert();
  const user = useUserContext();
  const { mutateAsync: createTicket, isPending } = useCreateSupportTicket();

  const form = useForm({
    initialValues: {
      category: "issue" as SupportTicketCategory,
      message: "",
      affectedAreas: [] as string[],
      relatedJobUids: [] as string[],
      relatedMountKeys: [] as string[], // "jobUid|frontFacingId"
      files: [] as FileWithPath[],
    },
    validate: {
      message: (value, values) => {
        const min = getSupportCategory(values.category).minMessageLength;
        if (value.trim().length < min) {
          return `Please write at least ${min} characters so we can help properly`;
        }
        // Excessive repeated characters, e.g. "...........". Whitespace is
        // exempt: pasted tables line up their columns with runs of spaces.
        if (/(\S)\1{4,}/.test(value)) {
          return "Detailed descriptions help us resolve things faster. Please avoid filler characters.";
        }
        return null;
      },
      affectedAreas: (value, values) => {
        if (values.category === "issue" && (!value || value.length === 0)) {
          return "Please select at least one affected area";
        }
        return null;
      },
    },
  });

  const copy = MESSAGE_COPY[form.values.category];
  const minLength = useMemo(
    () => getSupportCategory(form.values.category).minMessageLength,
    [form.values.category]
  );

  // Slim index only (shared cache with the POS picker); never full job docs.
  const { data: jobsIndex } = useQuery({
    queryKey: jobsIndexQueryKey(user.uid),
    queryFn: () => getJobsIndex(user.uid),
    enabled: !!user.uid && form.values.category === "issue",
    staleTime: 15 * 60 * 1000,
  });

  const jobOptions = useMemo(() => {
    if (!jobsIndex) return [];
    const seen = new Set<string>();
    return [...(jobsIndex as JobBasic[])]
      .sort((a, b) => b.jobId - a.jobId)
      .filter((job) => {
        if (!job.uid || seen.has(job.uid)) return false;
        seen.add(job.uid);
        return true;
      })
      .map((job) => ({
        value: job.uid,
        label: `#${job.jobId} · ${job.client.fullName || job.client.lastName}`,
      }));
  }, [jobsIndex]);

  const selectedJobs = useMemo(() => {
    if (!jobsIndex) return [];
    return (jobsIndex as JobBasic[]).filter((job) =>
      form.values.relatedJobUids.includes(job.uid)
    );
  }, [jobsIndex, form.values.relatedJobUids]);

  // Mount IDs grouped per selected job. Option values are keyed by the line
  // item uid, not the mount ID: studios often give several mounts on a job
  // the same ID (e.g. every mount is "M1"), and Mantine throws on duplicate
  // option values.
  const mountOptions = useMemo(
    () =>
      selectedJobs
        .map((job) => ({
          group: `#${job.jobId} · ${
            job.client.fullName || job.client.lastName
          }`,
          items: job.lineItems
            .filter(
              (lineItem): lineItem is JobBasicSpecimenLineItem =>
                "specimen" in lineItem
            )
            .map((lineItem) => ({
              value: `${job.uid}|${lineItem.uid}`,
              label: `${lineItem.frontFacingId} · ${
                lineItem.specimen.species ?? ""
              } ${lineItem.specimen.mountType ?? ""}`.trim(),
            })),
        }))
        .filter((group) => group.items.length > 0),
    [selectedJobs]
  );

  const buildRelatedJobs = (): SupportRelatedJob[] =>
    selectedJobs.map((job) => {
      const selectedLineItemUids = form.values.relatedMountKeys
        .filter((key) => key.startsWith(`${job.uid}|`))
        .map((key) => key.split("|")[1]);
      return {
        jobUid: job.uid,
        jobId: job.jobId,
        clientName: job.client.fullName || job.client.lastName,
        mountIds: job.lineItems
          .filter(
            (lineItem): lineItem is JobBasicSpecimenLineItem =>
              "specimen" in lineItem &&
              selectedLineItemUids.includes(lineItem.uid)
          )
          .map((lineItem) => lineItem.frontFacingId),
      };
    });

  const handleSubmit = form.onSubmit(async (values) => {
    const input: NewSupportTicketInput = {
      category: values.category,
      message: values.message.trim(),
      affectedAreas: values.category === "issue" ? values.affectedAreas : [],
      relatedJobs: values.category === "issue" ? buildRelatedJobs() : [],
      files: values.files as File[],
      context: {
        route: pathname ?? "",
        userAgent:
          typeof navigator !== "undefined" ? navigator.userAgent : "",
        isMobile,
        screen:
          typeof window !== "undefined"
            ? `${window.innerWidth}x${window.innerHeight}`
            : "",
        ...getSupportTrace(),
      },
    };

    try {
      const ticket = await createTicket(input);
      setAlert({
        type: "success",
        title: `Ticket #${ticket.ticketNumber} created`,
        content: (
          <Text size="sm">
            We will reply inside MountMonitor under Support.
          </Text>
        ),
      });
      form.reset();
      onCreated(ticket);
    } catch (error) {
      setAlert({
        type: "error",
        title: "Something went wrong creating your ticket",
        content: (
          <Text size="sm">
            {error instanceof Error
              ? error.message
              : "Please try again or email contact@mountmonitor.com"}
          </Text>
        ),
      });
    }
  });

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      {isPending && (
        <Portal>
          <AnimatedLoader text="Creating your ticket..." />
        </Portal>
      )}
      <Box flex={1} mih={0}>
        <Box h="100%" style={{ overflowY: "auto", overflowX: "hidden" }}>
          <Stack gap="md" p="md">
            {openTickets.length > 0 && onOpenTicket && (
              <Alert color="blue" variant="light" radius="md" p="sm">
                <Text size="sm">
                  {openTickets.length === 1
                    ? "You already have an open ticket. If this is about the same thing, reply there instead:"
                    : "You already have open tickets. If this is about the same thing, reply there instead:"}
                </Text>
                <Stack gap={2} mt={4}>
                  {openTickets.slice(0, 3).map((ticket) => (
                    <Anchor
                      key={ticket.uid}
                      size="sm"
                      fw={600}
                      onClick={() => onOpenTicket(ticket)}
                    >
                      #{ticket.ticketNumber} · {ticket.subject}
                    </Anchor>
                  ))}
                </Stack>
              </Alert>
            )}
            <Stack gap={4}>
              <InputLabel size="md">What is this about?</InputLabel>
              <SegmentedControl
                fullWidth
                size={isMobile ? "sm" : "md"}
                data={SUPPORT_CATEGORIES.map((c) => ({
                  value: c.value,
                  label: c.label,
                }))}
                value={form.values.category}
                onChange={(value) => {
                  form.setFieldValue(
                    "category",
                    value as SupportTicketCategory
                  );
                  form.clearFieldError("message");
                  form.clearFieldError("affectedAreas");
                }}
              />
            </Stack>

            {form.values.category === "issue" && (
              <>
                <MultiSelect
                  label="Related jobs (optional)"
                  description="Which job(s) does this involve? Helps us find the problem much faster."
                  size="md"
                  placeholder={
                    form.values.relatedJobUids.length === 0
                      ? "Search by job # or client"
                      : ""
                  }
                  data={jobOptions}
                  searchable
                  checkIconPosition="right"
                  styles={{
                    pill: {
                      backgroundColor: theme.colors.primary[5],
                      color: theme.white,
                      fontWeight: 500,
                    },
                  }}
                  value={form.values.relatedJobUids}
                  onChange={(value) => {
                    form.setFieldValue("relatedJobUids", value);
                    // Drop mount picks belonging to deselected jobs.
                    form.setFieldValue(
                      "relatedMountKeys",
                      form.values.relatedMountKeys.filter((key) =>
                        value.includes(key.split("|")[0])
                      )
                    );
                  }}
                />
                {mountOptions.length > 0 && (
                  <MultiSelect
                    label="Related mounts (optional)"
                    description="Narrow it down to specific mounts if you can"
                    size="md"
                    placeholder={
                      form.values.relatedMountKeys.length === 0
                        ? "Select mount IDs"
                        : ""
                    }
                    data={mountOptions}
                    searchable
                    checkIconPosition="right"
                    styles={{
                      pill: {
                        backgroundColor: theme.colors.primary[5],
                        color: theme.white,
                        fontWeight: 500,
                      },
                    }}
                    {...form.getInputProps("relatedMountKeys")}
                  />
                )}
              </>
            )}

            {form.values.category === "issue" && (
              <MultiSelect
                required
                label="Affected areas"
                description="Select the areas affected by this issue"
                size="md"
                placeholder={
                  form.values.affectedAreas.length === 0
                    ? "Select affected areas"
                    : ""
                }
                data={SUPPORT_AFFECTED_AREAS}
                checkIconPosition="right"
                searchable
                styles={{
                  pill: {
                    backgroundColor: theme.colors.primary[5],
                    color: theme.white,
                    fontWeight: 500,
                  },
                }}
                {...form.getInputProps("affectedAreas")}
              />
            )}

            <Textarea
              required
              label={copy.label}
              description={copy.description}
              placeholder={copy.placeholder}
              size="md"
              autosize
              minRows={8}
              maxRows={16}
              {...form.getInputProps("message")}
            />
            <Text size="xs" c="dimmed" mt={-8}>
              {form.values.message.trim().length}/{minLength} characters minimum
            </Text>

            <Stack gap={4}>
              <InputLabel size="md">Images</InputLabel>
              <InputDescription size="sm">
                {form.values.category === "issue"
                  ? "Screenshots of what you are seeing make a faster fix much more likely."
                  : "Optional. Attach anything that helps explain it."}
              </InputDescription>
              <Box mih={180}>
                <FileUploader
                  imagesOnly
                  compact
                  onFileResolve={(files) =>
                    form.setFieldValue("files", [...(files as FileWithPath[])])
                  }
                  dropzoneProps={{
                    styles: {
                      root: { minHeight: 180 },
                      inner: {
                        display: "flex",
                        alignItems: "center",
                        minHeight: 180,
                        width: "100%",
                        pointerEvents: "all",
                      },
                    },
                  }}
                />
              </Box>
            </Stack>
          </Stack>
        </Box>
      </Box>

      <SupportFooter>
        <Group justify="flex-end" wrap="nowrap">
          <Button type="submit" size="md" loading={isPending}>
            Submit ticket
          </Button>
        </Group>
      </SupportFooter>
    </form>
  );
};

export default NewSupportTicketForm;
